import dayjs from 'dayjs';
import logger from '../../utils/logger';
import { updateBatchOperations } from '../../services/api';

export type P6TaskDetails = {
  task_id: number;
  status_code: string | null;
  act_start_date: string | null;
  act_end_date: string | null;
  target_start_date?: string | null;
  complete_pct_type?: string | null;
  phys_complete_pct?: number | null;
  target_drtn_hr_cnt?: number | null;
  remain_drtn_hr_cnt?: number | null;
};

export type P6EditableTaskRsrcRow = {
  taskrsrc_id: number;
  act_start_date?: string | null;
  act_end_date?: string | null;
  target_qty: number | null;
  act_reg_qty: number | null;
  remain_qty: number | null;
  cost_per_qty: number | null;
  _new_act_reg_qty: number | null;
  _new_remain_qty: number | null;
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const round6 = (v: number): number => {
  return Math.round(v * 1_000_000) / 1_000_000;
};

const clampNonNegative = (v: number): number => {
  return v < 0 ? 0 : v;
};

const mergeDateWithTimeFrom = (params: {
  dateIso: string;
  timeSourceIso?: string | null;
}): string => {
  const datePart = dayjs(params.dateIso);
  const timeSource = params.timeSourceIso ? dayjs(params.timeSourceIso) : null;
  if (!datePart.isValid() || !timeSource || !timeSource.isValid()) return params.dateIso;
  return datePart
    .hour(timeSource.hour())
    .minute(timeSource.minute())
    .second(timeSource.second())
    .millisecond(timeSource.millisecond())
    .toDate()
    .toISOString();
};

export const computePhysCompletePctCostWeighted = (params: {
  rows: P6EditableTaskRsrcRow[];
  nextStatus: string | null;
}): number => {
  const { rows, nextStatus } = params;

  let denom = 0;
  let numer = 0;
  for (const r of rows) {
    const target = toNum(r.target_qty) ?? 0;
    const cost = toNum(r.cost_per_qty) ?? 0;

    let remain = toNum(r._new_remain_qty);
    if (nextStatus === 'TK_Complete') remain = 0;
    if (nextStatus === 'TK_NotStart') remain = target;
    remain = clampNonNegative(remain ?? 0);

    const earned = Math.min(target, Math.max(0, target - remain));
    denom += target * cost;
    numer += earned * cost;
  }

  if (!Number.isFinite(denom) || denom <= 0) return 0;
  let pct = (numer / denom) * 100;
  if (!Number.isFinite(pct)) pct = 0;
  if (pct < 0) pct = 0;
  if (pct > 100) pct = 100;
  return Math.round(pct * 100) / 100;
};

export const saveP6ProgressUpdate = async (params: {
  taskId: number;
  rows: P6EditableTaskRsrcRow[];
  taskDetails: P6TaskDetails;
  nextStatusCode: string | null;
  progressIso: string;
  user: string | null | undefined;
  database?: string;
}): Promise<{
  okCount: number;
  failCount: number;
  updatesSent: number;
  taskUpdated: boolean;
  computedPhysCompletePct?: number;
  computedRemainDrtnHrCnt?: number;
}> => {
  const { taskId, rows, taskDetails, nextStatusCode, progressIso, user, database = 'PPMTRAIN' } = params;

  const nextStatus = nextStatusCode != null ? String(nextStatusCode) : null;
  const currentStatus = taskDetails?.status_code != null ? String(taskDetails.status_code) : null;
  const localStatusChanged = nextStatus != null && nextStatus !== currentStatus;

  if (localStatusChanged && nextStatus === 'TK_Complete' && taskDetails?.act_start_date) {
    const start = dayjs(taskDetails.act_start_date);
    const end = dayjs(progressIso);
    if (start.isValid() && end.isValid() && end.isBefore(start)) {
      throw new Error('Actual Finish cannot be earlier than Actual Start');
    }
  }

  const shouldSetTaskActStart = localStatusChanged && nextStatus === 'TK_Active' && !taskDetails?.act_start_date;
  const shouldSetTaskActEnd = localStatusChanged && nextStatus === 'TK_Complete' && !taskDetails?.act_end_date;
  const shouldResetTaskActStart = localStatusChanged && nextStatus === 'TK_NotStart';
  const shouldResetTaskActEnd = localStatusChanged && nextStatus === 'TK_NotStart';
  const shouldForceRsrcRemainZero = localStatusChanged && nextStatus === 'TK_Complete';
  const shouldSetRsrcActEnd = localStatusChanged && nextStatus === 'TK_Complete';
  const shouldPropagateRsrcActStart = shouldSetTaskActStart;

  const updateMap = new Map<number, Record<string, any>>();

  const actStartIso = dayjs(progressIso).startOf('day').format('YYYY-MM-DDTHH:mm:ss');

  for (const r of rows) {
    const target = toNum(r.target_qty) ?? 0;
    const newActual = toNum(r._new_act_reg_qty) ?? 0;
    const nextRemain = round6(clampNonNegative(target - newActual));

    const oldActual = toNum(r.act_reg_qty) ?? 0;
    const oldRemain = toNum(r.remain_qty);
    const newRemain = toNum(r._new_remain_qty);

    const qtyChanged = oldActual !== newActual;
    const remainChanged = oldRemain !== newRemain;
    if (qtyChanged) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        act_reg_qty: newActual,
        remain_qty: nextRemain,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (!qtyChanged && remainChanged && newRemain != null) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        remain_qty: clampNonNegative(newRemain),
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (shouldPropagateRsrcActStart && !r.act_start_date) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        act_start_date: actStartIso,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (shouldResetTaskActStart && r.act_start_date) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        act_start_date: null,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (shouldResetTaskActStart) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        act_reg_qty: 0,
        remain_qty: round6(clampNonNegative(target)),
        act_end_date: null,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (shouldForceRsrcRemainZero) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        remain_qty: 0,
        act_end_date: progressIso,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }

    if (shouldSetRsrcActEnd && !r.act_end_date) {
      updateMap.set(r.taskrsrc_id, {
        ...(updateMap.get(r.taskrsrc_id) || {}),
        act_end_date: progressIso,
        update_date: progressIso,
        update_user: user ?? null,
        cost_qty_link_flag: 'Y',
        unshd_act_cost_flag: 'Y',
      });
    }
  }

  const updates = Array.from(updateMap.entries()).map(([id, data]) => ({ id, data }));

  if (!updates.length && !localStatusChanged) {
    return { okCount: 0, failCount: 0, updatesSent: 0, taskUpdated: false };
  }

  let computedPhysCompletePct: number | undefined;
  let computedRemainDrtnHrCnt: number | undefined;

  const operations: any[] = [];

  if (updates.length) {
    logger.debug('P6ProgressUpdate.save TASKRSRC payload', {
      context: 'P6ProgressUpdate',
      data: {
        table_name: 'TASKRSRC',
        id_field: 'taskrsrc_id',
        database,
        updates,
      },
    });
    operations.push({ table_name: 'TASKRSRC', id_field: 'taskrsrc_id', updates });
  }

  // Always include TASK update if we changed status or when we changed assignments
  if (localStatusChanged || updates.length) {
    const taskPayload: Record<string, any> = {
      recalc_task_flag: 'Y',
      update_date: progressIso,
      update_user: user ?? null,
    };

    // Keep TASK-level unit totals in sync with TASKRSRC quantities.
    // P6 UI (Status pane) reads these fields for Actual Labor Units / Actual Nonlabor Units.
    // When writing directly to the DB, these may not be recomputed automatically.
    const sumByType = (typeCode: string, field: 'act' | 'remain'): number => {
      const t = String(typeCode || '').trim().toLowerCase();
      let acc = 0;
      for (const r of rows || []) {
        const rt = String((r as any)?.rsrc_type ?? '').trim().toLowerCase();
        if (rt !== t) continue;
        const val = field === 'act'
          ? (toNum((r as any)?._new_act_reg_qty) ?? toNum((r as any)?.act_reg_qty) ?? 0)
          : (toNum((r as any)?._new_remain_qty) ?? toNum((r as any)?.remain_qty) ?? 0);
        acc += (val ?? 0);
      }
      return round6(acc);
    };

    const hasRsrcType = (rows || []).some((r) => String((r as any)?.rsrc_type ?? '').trim() !== '');
    if (hasRsrcType) {
      taskPayload.act_work_qty = sumByType('RT_Labor', 'act');
      taskPayload.remain_work_qty = sumByType('RT_Labor', 'remain');
      taskPayload.act_equip_qty = sumByType('RT_Equip', 'act');
      taskPayload.remain_equip_qty = sumByType('RT_Equip', 'remain');
    }

    if (localStatusChanged) {
      taskPayload.status_code = nextStatus;
      if (shouldSetTaskActStart) taskPayload.act_start_date = actStartIso;
      if (shouldSetTaskActEnd) taskPayload.act_end_date = progressIso;
      if (shouldResetTaskActStart) taskPayload.act_start_date = null;
      if (shouldResetTaskActEnd) taskPayload.act_end_date = null;
    }

    if (taskDetails?.complete_pct_type === 'CP_Phys') {
      computedPhysCompletePct = computePhysCompletePctCostWeighted({ rows, nextStatus });
      taskPayload.phys_complete_pct = computedPhysCompletePct;

      const targetDrtn = toNum(taskDetails?.target_drtn_hr_cnt);
      if (targetDrtn != null) {
        let nextRemainDrtn = targetDrtn * (1 - (computedPhysCompletePct ?? 0) / 100);
        if (nextStatus === 'TK_Complete') nextRemainDrtn = 0;
        if (nextStatus === 'TK_NotStart') nextRemainDrtn = targetDrtn;
        nextRemainDrtn = clampNonNegative(nextRemainDrtn);
        if (nextRemainDrtn > targetDrtn) nextRemainDrtn = targetDrtn;
        computedRemainDrtnHrCnt = round6(nextRemainDrtn);
        taskPayload.remain_drtn_hr_cnt = computedRemainDrtnHrCnt;
      }
    }

    logger.debug('P6ProgressUpdate.save TASK payload', {
      context: 'P6ProgressUpdate',
      data: {
        table_name: 'TASK',
        id_field: 'task_id',
        id: taskId,
        database,
        data: taskPayload,
      },
    });
    operations.push({ table_name: 'TASK', id_field: 'task_id', updates: [{ id: taskId, data: taskPayload }] });
  }

  const resp = await updateBatchOperations({
    operations,
    database,
    rollback_on_error: true,
  });

  const okCount = Number(resp?.updated ?? 0);
  const failCount = Number(resp?.failed ?? 0);
  const taskUpdated = operations.some((o) => String(o?.table_name ?? '').toUpperCase().endsWith('TASK'));

  return {
    okCount,
    failCount,
    updatesSent: updates.length,
    taskUpdated,
    computedPhysCompletePct,
    computedRemainDrtnHrCnt,
  };
};
